import { forwardRef, type ReactElement, type ReactNode, type Ref } from 'react';
import { Button as AriaButton, type ButtonProps as AriaButtonProps } from 'react-aria-components';
import { Spinner } from '#components/spinner/spinner';
import { getButtonClassNames, resolveButtonVariantProps } from './button-utils';

type Size = 'medium' | 'small';

/** @deprecated Use `medium` instead — `large` falls back to `medium` at runtime. Will be removed in a future release. */
type DeprecatedSize = 'large';

/** @deprecated Use `small` instead — `xsmall` falls back to `small` at runtime. Will be removed in a future release. */
type DeprecatedXSmallSize = 'xsmall';

export type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'tertiary';

// ---------------------------------------------------------------------------
// ButtonStyleProps — shared visual props used by Button, InternalLink, ExternalLink.
// ---------------------------------------------------------------------------

export interface ButtonStyleProps {
  /** @default 'primary' for label buttons, 'tertiary' for icon-only */
  variant?: ButtonVariant;
  /** Render as an icon-only button. Only `secondary` and `tertiary` variants are supported; others fall back to `tertiary`. */
  iconOnly?: boolean;
  size?: Size | DeprecatedSize | DeprecatedXSmallSize;
  stretch?: boolean;
  /** Left icon — only rendered when `variant="tertiary"` and `iconOnly` is false. */
  iconLeft?: ReactNode;
  /** Right icon — only rendered when `variant="tertiary"` and `iconOnly` is false. Takes the spinner slot; `isPending`/`isLoading` have no effect when set. */
  iconRight?: ReactNode;
}

// ---------------------------------------------------------------------------
// ButtonVariantProps — ButtonStyleProps + content/state props for the Button component.
// ---------------------------------------------------------------------------

export interface ButtonVariantProps extends ButtonStyleProps {
  /** Takes priority over `children` in rendering. */
  label?: string;
  /**
   * @deprecated Use `label` and `rightIcon`/`leftIcon` or `icon` instead.
   */
  children?: ReactNode;
  /** The icon to display in icon-only mode. Prefer over `children` for new usages. */
  icon?: ReactNode;
  isPending?: boolean;
  /**
   * @deprecated Use `isPending` instead.
   */
  isLoading?: boolean;
}

export type BaseButtonProps = Omit<AriaButtonProps, 'className' | 'children'> &
  ButtonVariantProps & { ref?: Ref<HTMLButtonElement>; className?: string };

export { buttonStyles, getButtonClassNames, resolveButtonVariantProps } from './button-utils';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const Button = forwardRef<HTMLButtonElement, BaseButtonProps>(function Button(
  {
    children,
    label,
    variant,
    size,
    iconOnly = false,
    stretch = false,
    isLoading = false,
    isPending,
    isDisabled,
    className,
    icon,
    iconLeft,
    iconRight,
    ...props
  },
  ref: Ref<HTMLButtonElement>
): ReactElement {
  const { effectiveVariant, effectiveIconLeft, effectiveIconRight, effectiveSize } =
    resolveButtonVariantProps({
      variant,
      iconOnly,
      iconLeft,
      iconRight,
      size,
    });
  const isIconOnlyLoading = iconOnly && (isLoading || (isPending ?? false));

  return (
    <AriaButton
      className={getButtonClassNames({
        variant: effectiveVariant,
        size: effectiveSize,
        iconOnly,
        stretch,
        className: typeof className === 'string' ? className : undefined,
      })}
      data-variant={effectiveVariant}
      isDisabled={isDisabled}
      isPending={isPending ?? isLoading}
      ref={ref}
      {...props}
    >
      <>
        {effectiveIconLeft}
        {!isIconOnlyLoading && (label ?? icon ?? children)}
        {effectiveIconRight ?? ((isPending ?? isLoading) ? <Spinner size="small" /> : null)}
      </>
    </AriaButton>
  );
});
